import { Reducer, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { IssueItem } from "@ServerTypes/issues/issues.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

type IssuesState = Record<string, IssueItem | undefined>;

/**
 * `setIssuesCommentsData` payload. `data` is whichever comments payload the caller just received:
 * the full list from `getCommentsData` or the single comment from `saveCommentsData`. Both are
 * modelled as open records by `comments.schema.ts` (`z.record(z.string(), z.unknown())`), so only
 * `_id` is read here. `isEdit` marks the save path as an in-place update rather than a prepend.
 */
type SetIssuesCommentsDataPayload = {
  issueId: ObjectIdString;
  data: Record<string, unknown> | Record<string, unknown>[];
  isEdit?: boolean;
};

/** `deleteIssuesCommentsData` payload: the removed comment and the issue whose map it hangs off. */
type DeleteIssuesCommentsDataPayload = {
  issueId: ObjectIdString;
  commentId: ObjectIdString;
};

/**
 * The comments map this slice keeps under `IssueItem.comments`, which `issues.schema.ts` declares
 * as `z.unknown()`. Only this reducer writes it, always as commentId -> comment record; the comment
 * records themselves stay opaque, exactly as `comments.schema.ts` models them.
 */
function isCommentsMap(value: unknown): value is Record<string, Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * A comment's own `_id`, used as its key in the map above. Comment payloads are open records
 * (`z.record(z.string(), z.unknown())`), so `_id` arrives untyped — and a comment without a string
 * `_id` has no key to be stored under, so it is dropped rather than filed under "undefined".
 */
function commentKey(comment: Record<string, unknown>): string | null {
  return typeof comment._id === "string" ? comment._id : null;
}

const initialIssuesState: IssuesState = {};

const issuesSlice = createSlice({
  name: "issues",
  initialState: initialIssuesState,
  reducers: {
    setIssuesData: (state: IssuesState, action: PayloadAction<IssueItem[]>) => {
      for (const issue of action.payload) {
        if (state[issue.id]) {
          state[issue.id] = {
            ...state[issue.id],
            ...issue,
          };
        } else {
          state[issue.id] = issue;
        }
      }
    },
    updateIssuesData: (_state: IssuesState, action: PayloadAction<IssueItem[]>) =>
      action.payload.reduce<IssuesState>((prev, curr) => {
        if (curr.id) {
          return { ...prev, [curr.id]: curr };
        }
        return { ...prev };
      }, {}),
    modifyIssuesData: (state: IssuesState, action: PayloadAction<IssueItem>) => {
      const issue = action.payload;
      state[issue.id] = {
        ...state[issue.id],
        ...issue,
      };
    },
    setIssuesCommentsData: (state: IssuesState, action: PayloadAction<SetIssuesCommentsDataPayload>) => {
      const { issueId, data, isEdit } = action.payload;
      const issueData: IssueItem = state[issueId] ?? { id: issueId };
      let commentsData: Record<string, Record<string, unknown>> = {};
      if (Array.isArray(data)) {
        commentsData = data.reduce<Record<string, Record<string, unknown>>>((prev, curr) => {
          const key = commentKey(curr);
          return key ? { ...prev, [key]: curr } : prev;
        }, {});
      } else {
        const key = commentKey(data);
        const existingComments = isCommentsMap(issueData.comments) ? issueData.comments : null;
        if (key && existingComments) {
          commentsData = isEdit
            ? {
                ...existingComments,
                [key]: {
                  ...existingComments[key],
                  ...data,
                },
              }
            : {
                [key]: data,
                ...existingComments,
              };
        } else if (key) {
          commentsData = {
            [key]: {
              ...data,
            },
          };
        } else if (existingComments) {
          commentsData = existingComments;
        }
      }
      state[issueId] = { ...issueData, comments: commentsData };
    },
    deleteIssuesCommentsData: (state: IssuesState, action: PayloadAction<DeleteIssuesCommentsDataPayload>) => {
      const { issueId, commentId } = action.payload;
      const issue = state[issueId];
      if (issue && isCommentsMap(issue.comments)) {
        delete issue.comments[commentId];
      }
      state[issueId] = issue;
    },
    emptyIssuesData: () => ({}),
    mergeIssuesData: (state: IssuesState, action: PayloadAction<{ data: IssueItem; editId?: string }>) => {
      const { data, editId } = action.payload;
      const issueId = data.id;
      if (editId) {
        state[editId] = { ...state[editId], ...data };
      } else {
        return { [issueId]: { ...data }, ...state };
      }
    },
    removeIssueFromState: (state: IssuesState, action: PayloadAction<{ ids: string[] }>) => {
      const { ids } = action.payload;
      ids.forEach((id: string) => {
        delete state[id];
      });
    },
    deleteIssuesData: (state: IssuesState, action: PayloadAction<string[]>) => {
      const issues = action.payload;
      issues.forEach((issue) => {
        delete state[issue];
      });
    },
    initializeIssuesData: () => initialIssuesState,
  },
});

export const {
  updateIssuesData,
  setIssuesData,
  modifyIssuesData,
  setIssuesCommentsData,
  deleteIssuesCommentsData,
  emptyIssuesData,
  mergeIssuesData,
  removeIssueFromState,
  deleteIssuesData,
  initializeIssuesData,
} = issuesSlice.actions;

const issueReducer: Reducer<IssuesState> = issuesSlice.reducer;
export default issueReducer;
